import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { coverPatchFor } from './store/coverPatch';
import {
  FRONT_STYLES, BACK_STYLES, FRONT_TO_BACK_PAIR, TONES, PHRASE_MAX, readRenderedText, selectedStyleId,
  suggestedStyleId, toneApplies, fieldValue, fieldPatch, dateSuggestions
} from './coverPanelModel';
import { CoverFormatMiniPreview } from './CoverFormatMiniPreview';
import { formatAspectRatio } from '../../../utils/printFormats';
import '../BookLuxe.css';
import './AtelierCoverPanel.css';

// PANNEAU COUVERTURE / 4e (restructure le 2026-10-12, maquette "Atelier Bookipix").
//
//  - un selecteur [ Couverture | 4e de couverture ] collant en haut, a la place des
//    anciens liens « Voir la 4e... » / « Revenir a la couverture » ;
//  - recto : Titre -> Style -> Couleurs -> « Plus de details » (replie : sous-titre,
//    date, texte au-dessus du titre) ; 4e : Phrase -> Style -> Couleurs -> mention ;
//  - plus de tuile « Automatique » : un style est SUGGERE (pastille), un lien
//    « Revenir au style suggere » le restaure apres un choix manuel — meme principe
//    pour la couleur, le texte au-dessus du titre et la phrase de 4e ;
//  - le style de l'album (Nu / Filet / Encadre) n'est plus ici : il vit en tete de
//    l'onglet « Mise en page » (reglage « Tout le livre », voir AtelierLayoutPanel) ;
//  - le titre, le sous-titre et la date se modifient aussi directement sur la
//    couverture (CoverTextOverlay).
//
// Le panneau n'a PAS d'etat propre pour les donnees du livre : il affiche ce que le
// store contient et lui envoie des modifications (onEditCover) — meme historique, meme
// sauvegarde, meme « Annuler » que les pages. Le seul etat local est l'ouverture de la
// section « Plus de details ». Les champs sont controles : la page se met a jour en
// direct (apres un court silence) sans que le champ perde le focus.
//
// Ne choisit PAS la photo (se fait en cliquant sur la couverture, voir
// CoverPhotoOverlay).

const normalizeText = (value) => (typeof value === 'string' ? value : '');

// Champs saisis au clavier : enregistres apres un court silence, un seul pas d'historique.
const TYPED_FIELDS = new Set(['title', 'subtitle', 'dateLabel', 'kickerText', 'closingPhraseText']);

function StyleCard({ style, selected, suggested, ratio, photoUrl, label, description, onSelect, t }) {
  return (
    <button
      type="button"
      className={`cvp-card ${selected ? 'is-selected' : ''}`}
      aria-pressed={selected}
      onClick={() => onSelect(style.id)}
    >
      {selected && (
        <span className="cvp-card-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
        </span>
      )}
      {suggested && !selected && <span className="cvp-card-sug">{t('coverPanel.suggested')}</span>}
      <span className="cvp-card-preview">
        <CoverFormatMiniPreview shape={style.shape} ratio={ratio} photoUrl={null} />
      </span>
      <b className="cvp-card-label">{label}</b>
      <span className="cvp-card-desc">{description}</span>
      {selected && suggested && <span className="cvp-tag cvp-card-tag">{t('coverPanel.suggested')}</span>}
    </button>
  );
}

function Field({ id, label, hint, children }) {
  return (
    <div className="cvp-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <small>{hint}</small>}
    </div>
  );
}

function AtelierCoverPanel({
  book, face, onEditCover, onSelectFace, coverPhotoUrl, coverHtml, backCoverHtml,
  // Photos de la bibliotheque (pour en attribuer une quand un style avec photo est applique) et ouverture du
  // choix de photo de la face (bouton « Changer » du message).
  libraryPhotos = [], onChangePhoto
}) {
  const { t, i18n } = useTranslation('atelier');
  const [moreOpen, setMoreOpen] = useState(false);
  const isFront = face === 'front';
  const ratio = formatAspectRatio(book?.print_format);
  const overrides = useMemo(
    () => (book?.cover_overrides && typeof book.cover_overrides === 'object' ? book.cover_overrides : {}),
    [book?.cover_overrides]
  );
  // « Les contributeurs » n'a de sens que sur un livre collectif (le moteur repliait en silence sur « Sobre »).
  const styles = (isFront ? FRONT_STYLES : BACK_STYLES).filter(
    (style) => style.id !== 'BACK_CONTRIB' || (book && book.collection_mode !== 'solo')
  );
  const tone = TONES.some((entry) => entry.token === overrides.tone) ? overrides.tone : '';

  // Style de la couverture tel qu'il est AFFICHE (explicite, sinon rendu par le moteur) :
  // la suggestion de la 4e en depend.
  const frontStyleId = selectedStyleId({ face: 'front', overrides, html: coverHtml });
  const selectedId = isFront ? frontStyleId : selectedStyleId({ face: 'back', overrides, html: backCoverHtml });
  const computedSuggestion = suggestedStyleId({
    face,
    overrides,
    frontStyleId: frontStyleId || suggestedStyleId({ face: 'front', overrides })
  });
  // Recto en « automatique » (aucun style choisi a la main) : le style AFFICHE est, par definition, celui que
  // le moteur suggere (il depend de la photo posee et de sa qualite) — jamais de lien « Revenir » dans ce cas.
  const explicitFront = overrides.frontVariant;
  const frontIsAuto = !FRONT_STYLES.some((style) => style.id === explicitFront);
  const suggestedId = isFront && frontIsAuto && selectedId ? selectedId : computedSuggestion;

  const toastLabel = isFront ? t('store.toasts.coverUpdated') : t('store.toasts.backCoverUpdated');
  const update = (patch, key) => onEditCover(coverPatchFor(patch), {
    label: toastLabel,
    coalesceKey: `cover:${key || Object.keys(patch).join('+')}`,
    fast: !Object.keys(patch).some((field) => TYPED_FIELDS.has(field))
  });
  // Choix dont l'effet n'est pas evident : message « Annuler ».
  const updateWithUndo = (patch, message) => onEditCover(coverPatchFor(patch), { label: message, toast: true, fast: true });

  // Photo attribuee d'office a un style avec photo quand la face n'en a pas : la plus recente de la
  // bibliotheque (jamais celle du recto pour la 4e). Retourne null si rien a attribuer.
  const photoKey = isFront ? 'frontPhotoId' : 'backPhotoId';
  const pickPhotoFor = (style) => {
    if (!style || !style.hasPhoto || overrides[photoKey] || !libraryPhotos.length) return null;
    const byNewest = [...libraryPhotos].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')) || 0);
    const candidate = byNewest.find((photo) => isFront || photo.id !== overrides.frontPhotoId) || byNewest[0];
    return candidate ? candidate.id : null;
  };

  const handleChooseStyle = (id) => {
    const style = styles.find((entry) => entry.id === id);
    const autoPhoto = pickPhotoFor(style);
    const extra = autoPhoto ? { [photoKey]: autoPhoto } : {};
    const meta = autoPhoto && onChangePhoto
      ? { toastExtra: { label: t('coverPanel.changePhoto'), onClick: () => onChangePhoto(face) } }
      : {};
    if (isFront) {
      const paired = FRONT_TO_BACK_PAIR[id];
      onEditCover(coverPatchFor(paired ? { frontVariant: id, backVariant: paired, ...extra } : { frontVariant: id, ...extra }), {
        label: autoPhoto ? t('coverPanel.toasts.photoChosen') : (paired ? t('coverPanel.toasts.frontStylePaired') : t('coverPanel.toasts.frontStyle')),
        toast: true,
        fast: true,
        ...meta
      });
    } else {
      onEditCover(coverPatchFor({ backVariant: id, ...extra }), {
        label: autoPhoto ? t('coverPanel.toasts.photoChosen') : t('coverPanel.toasts.backStyle'),
        toast: true,
        fast: true,
        ...meta
      });
    }
  };
  // Recto : 'AUTO' = le moteur choisit (et suit la photo posee). 4e : celle qui s'accorde au recto.
  const handleRevertStyle = () => updateWithUndo(
    isFront ? { frontVariant: 'AUTO' } : { backVariant: suggestedId },
    t('coverPanel.toasts.styleReverted')
  );
  const handleChooseTone = (token) => updateWithUndo({ tone: token }, t('coverPanel.toasts.tone'));
  const handleRevertTone = () => updateWithUndo({ tone: '' }, t('coverPanel.toasts.toneReverted'));
  const canTone = toneApplies({ face, styleId: selectedId || suggestedId });

  // Texte au-dessus du titre et phrase de 4e : UN champ, trois etats — texte suggere par le
  // moteur (lu dans l'apercu), texte personnalise, ou vide = « aucun ».
  const kickerMode = ['custom', 'none'].includes(overrides.kickerMode) ? overrides.kickerMode : 'auto';
  const kickerAuto = useMemo(() => readRenderedText(coverHtml, '.cvr-kicker'), [coverHtml]);
  const kickerValue = fieldValue({ mode: kickerMode, text: normalizeText(overrides.kickerText), autoText: kickerAuto });
  const phraseMode = ['custom', 'none'].includes(overrides.closingPhraseMode) ? overrides.closingPhraseMode : 'auto';
  const phraseAuto = useMemo(() => readRenderedText(backCoverHtml, '.cvr-back-phrase'), [backCoverHtml]);
  const phraseValue = fieldValue({ mode: phraseMode, text: normalizeText(overrides.closingPhraseText), autoText: phraseAuto });

  const dates = useMemo(
    () => dateSuggestions({ eventDate: book?.event_date, t, locale: i18n.language }),
    [book?.event_date, i18n.language, t]
  );
  const rawSuggestions = t('coverPanel.phraseSuggestions', { returnObjects: true });
  const phraseSuggestions = Array.isArray(rawSuggestions) ? rawSuggestions : [];
  const dateValue = normalizeText(overrides.dateLabel);

  const segment = (
    <div className="cvp-sticky">
      <div className="cvp-seg" role="group" aria-label={t('coverPanel.faceGroup')}>
        <button type="button" aria-pressed={isFront} onClick={() => onSelectFace('front')}>{t('coverPanel.faceFront')}</button>
        <button type="button" aria-pressed={!isFront} onClick={() => onSelectFace('back')}>{t('coverPanel.faceBack')}</button>
      </div>
    </div>
  );

  const styleSection = (
    <section className="cvp-section" aria-labelledby="cvp-style-h">
      <h2 id="cvp-style-h">{t('coverPanel.styleLabel')}</h2>
      <p className="cvp-hint">{isFront ? t('coverPanel.frontStyleHint') : t('coverPanel.backStyleHint')}</p>
      <div className="cvp-grid">
        {styles.map((style) => (
          <StyleCard
            key={style.id}
            style={style}
            selected={selectedId === style.id}
            suggested={suggestedId === style.id}
            ratio={ratio}
            photoUrl={coverPhotoUrl}
            label={t(`coverPanel.${isFront ? 'frontFormats' : 'backFormats'}.${style.id}`)}
            description={t(`coverPanel.${isFront ? 'frontFormatDescs' : 'backFormatDescs'}.${style.id}`)}
            onSelect={handleChooseStyle}
            t={t}
          />
        ))}
      </div>
      {selectedId !== suggestedId && (
        <button type="button" className="cvp-link" onClick={handleRevertStyle}>{t('coverPanel.revertStyle')}</button>
      )}
    </section>
  );

  const toneSection = (
    <section className="cvp-section" aria-labelledby="cvp-tone-h">
      <h2 id="cvp-tone-h">{t('coverPanel.toneLabel')} <small>{t('coverPanel.toneScope')}</small></h2>
      {canTone ? (
        <>
          <div className="cvp-tones" role="group" aria-label={t('coverPanel.toneLabel')}>
            {TONES.map((entry) => (
              <button
                key={entry.token}
                type="button"
                className={`cvp-tone ${tone === entry.token ? 'is-selected' : ''}`}
                style={{ background: entry.bg, color: entry.ink }}
                aria-pressed={tone === entry.token}
                aria-label={t(`coverPanel.tones.${entry.token}`)}
                title={t(`coverPanel.tones.${entry.token}`)}
                onClick={() => handleChooseTone(entry.token)}
              >
                {tone === entry.token ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
                ) : 'Aa'}
              </button>
            ))}
          </div>
          {tone === '' ? (
            <span className="cvp-tag">{t('coverPanel.toneMatched')}</span>
          ) : (
            <button type="button" className="cvp-link" onClick={handleRevertTone}>{t('coverPanel.revertTone')}</button>
          )}
        </>
      ) : (
        <p className="cvp-hint">{t('coverPanel.tonePhotoNote')}</p>
      )}
    </section>
  );

  if (isFront) {
    return (
      <div className="atelier-layout-panel atelier-cover-panel cvp">
        {segment}
        <Field id="cvp-title" label={t('coverPanel.bookTitleLabel')} hint={t('coverPanel.directEditHint')}>
          <input
            id="cvp-title"
            type="text"
            className="cvp-input"
            value={normalizeText(book?.title)}
            onChange={(event) => update({ title: event.target.value })}
            placeholder={t('coverPanel.bookTitlePlaceholder')}
            maxLength={180}
            autoComplete="off"
          />
        </Field>

        {styleSection}
        {toneSection}

        <div className="cvp-more">
          <button type="button" className="cvp-more-btn" aria-expanded={moreOpen} aria-controls="cvp-more-body" onClick={() => setMoreOpen((open) => !open)}>
            <span>{t('coverPanel.moreDetails')}</span>
            <small>{t('coverPanel.moreDetailsHint')}</small>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
          </button>
          {moreOpen && (
            <div id="cvp-more-body" className="cvp-more-body">
              <Field id="cvp-sub" label={t('coverPanel.subtitleLabel')}>
                <input
                  id="cvp-sub"
                  type="text"
                  className="cvp-input"
                  value={normalizeText(overrides.subtitle)}
                  onChange={(event) => update({ subtitle: event.target.value })}
                  placeholder={t('coverPanel.subtitlePlaceholder')}
                  maxLength={220}
                  autoComplete="off"
                />
              </Field>
              <Field id="cvp-date" label={t('coverPanel.dateLabel')}>
                <input
                  id="cvp-date"
                  type="text"
                  className="cvp-input"
                  value={dateValue}
                  onChange={(event) => update({ dateLabel: event.target.value })}
                  placeholder={t('coverPanel.datePlaceholder')}
                  maxLength={20}
                  autoComplete="off"
                />
                <div className="cvp-chips">
                  {dates.map((entry) => (
                    <button key={entry} type="button" className="cvp-chip" aria-pressed={dateValue === entry} onClick={() => update({ dateLabel: entry }, 'dateLabel')}>
                      {entry}
                    </button>
                  ))}
                </div>
              </Field>
              <Field id="cvp-kicker" label={t('coverPanel.kickerLabel')}>
                <input
                  id="cvp-kicker"
                  type="text"
                  className="cvp-input"
                  value={kickerValue}
                  onChange={(event) => {
                    const next = fieldPatch(event.target.value);
                    update({ kickerMode: next.mode, kickerText: next.text }, 'kicker');
                  }}
                  placeholder={t('coverPanel.kickerPlaceholder')}
                  maxLength={60}
                  autoComplete="off"
                />
                {kickerMode !== 'auto' && (
                  <button type="button" className="cvp-link" onClick={() => update({ kickerMode: 'auto' }, 'kicker')}>{t('coverPanel.revertText')}</button>
                )}
              </Field>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="atelier-layout-panel atelier-cover-panel cvp">
      {segment}
      <Field id="cvp-phrase" label={t('coverPanel.closingPhraseLabel')}>
        <textarea
          id="cvp-phrase"
          className="cvp-input cvp-textarea"
          rows={3}
          maxLength={PHRASE_MAX}
          value={phraseValue}
          placeholder={t('coverPanel.closingPhrasePlaceholder')}
          onChange={(event) => {
            const next = fieldPatch(event.target.value);
            update({ closingPhraseMode: next.mode, closingPhraseText: next.text }, 'phrase');
          }}
        />
        <div className="cvp-count-row">
          <span className="cvp-chips">
            {phraseSuggestions.map((entry) => (
              <button key={entry} type="button" className="cvp-chip" aria-pressed={phraseValue === entry} onClick={() => update({ closingPhraseMode: 'custom', closingPhraseText: entry }, 'phrase')}>
                {entry.replace(/[.]$/, '')}
              </button>
            ))}
          </span>
          <small className="cvp-count">{phraseValue.length} / {PHRASE_MAX}</small>
        </div>
        {phraseMode !== 'auto' && (
          <button type="button" className="cvp-link" onClick={() => update({ closingPhraseMode: 'auto' }, 'phrase')}>{t('coverPanel.revertText')}</button>
        )}
      </Field>

      {styleSection}
      {toneSection}

      <div className="cvp-toggle-row">
        <div>
          <b>{t('coverPanel.showBrandMarkLabel')}</b>
          <span>{t('coverPanel.showBrandMarkHint')}</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={overrides.showBookipixMark !== false}
          aria-label={t('coverPanel.showBrandMarkLabel')}
          className={`cvp-switch ${overrides.showBookipixMark !== false ? 'is-on' : ''}`}
          onClick={() => update({ showBookipixMark: overrides.showBookipixMark === false }, 'mark')}
        >
          <i />
        </button>
      </div>
    </div>
  );
}

export default AtelierCoverPanel;
